import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App.tsx";
import { ConnectivityBanner } from "./shell/ConnectivityBanner";
import { MutationToaster, toastError } from "./components/ui/mutationToast";
import { bootNative } from "./native/boot";
import "./index.css";

// APP-NATIVE-01: a no-op in the browser; in the iPhone / iPad app it opens on /m.
bootNative();

const queryClient = new QueryClient({
  // MUTATION-TOAST-01 (review 2026-11-02 P1 #11): 754 writes, 103 handlers — a failed save with no
  // handler of its own used to vanish. Any mutation that does not declare onError now surfaces its
  // failure in the shared toaster; mutations with their own handler keep it (no double message).
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return;
      const detail = (error as Error)?.message?.trim();
      toastError(detail ? `Couldn't save — ${detail.slice(0, 160)}` : "Couldn't save — try again.");
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30s default freshness
      refetchOnWindowFocus: false,
      refetchOnReconnect: true, // refetch when the browser regains connectivity
      retry: 3, // ride out a brief blip with exponential backoff
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15000),
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConnectivityBanner />
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <MutationToaster />
    </QueryClientProvider>
  </StrictMode>,
);
